/**
 * User Permissions Modal Component
 * Allows super_admins to manage user area-based access permissions
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Shield, History, AlertCircle, Zap } from 'lucide-react';
import { api } from '../../services/api';
import {
  AREAS,
  AREA_DISPLAY,
  AREA_ACCESS_PRESETS,
  type Area,
  type AccessLevel,
  type AreaAccessMap,
} from '../../hooks/usePermission';
import { useLanguage } from '../../contexts/LanguageContext';

interface UserPermissionsProps {
  userId: string;
  userEmail: string;
  onClose: () => void;
}

interface AuditEntry {
  id: string;
  action: string;
  previous_value: string | null;
  new_value: string | null;
  reason: string | null;
  created_at: string;
}

// Access level display
const getAccessLevelDisplay = (t: (key: string, fallback: string) => string): Record<AccessLevel, { label: string; color: string }> => ({
  none: { label: t('userPermissions.accessLevels.none', 'No Access'), color: 'bg-gray-100 text-gray-600' },
  read: { label: t('userPermissions.accessLevels.read', 'Read Only'), color: 'bg-blue-100 text-blue-700' },
  edit: { label: t('userPermissions.accessLevels.edit', 'Full Access'), color: 'bg-green-100 text-green-700' },
});

export function UserPermissions({ userId, userEmail, onClose }: UserPermissionsProps) {
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const ACCESS_LEVEL_DISPLAY = getAccessLevelDisplay(t);
  const [selectedTab, setSelectedTab] = useState<'access' | 'history'>('access');
  const [localAreaAccess, setLocalAreaAccess] = useState<AreaAccessMap>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch user permissions details
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: () => api.getUserPermissions(userId),
  });

  // Initialize local state when data loads
  useEffect(() => {
    if (data?.areaAccess) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Sync server data to local form state for editing
      setLocalAreaAccess(data.areaAccess);
      setHasChanges(false);
    }
  }, [data?.areaAccess]);

  // Save area access mutation
  const saveAreaAccessMutation = useMutation({
    mutationFn: () => api.setUserAreaAccess(userId, localAreaAccess as Record<string, AccessLevel>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-permissions', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setHasChanges(false);
    },
  });

  const handleAreaAccessChange = (area: Area, level: AccessLevel) => {
    const newAccess = { ...localAreaAccess };
    if (level === 'none') {
      delete newAccess[area];
    } else {
      newAccess[area] = level;
    }
    setLocalAreaAccess(newAccess);
    setHasChanges(true);
  };

  const applyPreset = (presetKey: string) => {
    const preset = AREA_ACCESS_PRESETS[presetKey];
    if (preset) {
      setLocalAreaAccess({ ...preset.access });
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    saveAreaAccessMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-2xl">
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <AlertCircle className="w-12 h-12 text-red-500" />
            <p className="text-red-600">Failed to load user permissions</p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const auditLog = data?.auditLog || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">User Permissions</h2>
            <p className="text-sm text-gray-500">{userEmail}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6">
          <button
            onClick={() => setSelectedTab('access')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'access'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Shield size={16} className="inline mr-2" />
            Access Levels
          </button>
          <button
            onClick={() => setSelectedTab('history')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <History size={16} className="inline mr-2" />
            History
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Access Levels Tab */}
          {selectedTab === 'access' && (
            <div className="space-y-6">
              {/* Quick Presets */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={16} className="text-amber-500" />
                  <h3 className="text-sm font-medium text-gray-700">Quick Presets</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(AREA_ACCESS_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => applyPreset(key)}
                      className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Area Access Grid */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Area Permissions</h3>
                <div className="space-y-2">
                  {AREAS.map((area) => {
                    const areaInfo = AREA_DISPLAY[area];
                    const currentLevel = localAreaAccess[area] || 'none';

                    return (
                      <div
                        key={area}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">{areaInfo.label}</p>
                          <p className="text-xs text-gray-500">{areaInfo.description}</p>
                        </div>
                        <select
                          value={currentLevel}
                          onChange={(e) => handleAreaAccessChange(area, e.target.value as AccessLevel)}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg border-0 focus:ring-2 focus:ring-primary-500 cursor-pointer ${ACCESS_LEVEL_DISPLAY[currentLevel].color}`}
                        >
                          <option value="none" className="bg-white text-gray-900">No Access</option>
                          <option value="read" className="bg-white text-gray-900">Read Only</option>
                          <option value="edit" className="bg-white text-gray-900">Full Access</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Effective Permissions Summary */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Effective Permissions ({data?.effectivePermissions?.length || 0})
                </h3>
                {data?.effectivePermissions?.length === 0 ? (
                  <p className="text-sm text-gray-500">No admin permissions</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {data?.effectivePermissions?.map((perm: string) => (
                      <span
                        key={perm}
                        className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                      >
                        {perm}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Button */}
              {hasChanges && (
                <div className="sticky bottom-0 bg-white pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-amber-600">You have unsaved changes</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setLocalAreaAccess(data?.areaAccess || {});
                          setHasChanges(false);
                        }}
                        className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Discard
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saveAreaAccessMutation.isPending}
                        className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                      >
                        {saveAreaAccessMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                  {saveAreaAccessMutation.isError && (
                    <p className="text-sm text-red-500 mt-2">
                      {saveAreaAccessMutation.error instanceof Error ? saveAreaAccessMutation.error.message : 'Failed to save changes'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {selectedTab === 'history' && (
            <div className="space-y-4">
              {auditLog.length === 0 ? (
                <p className="text-sm text-gray-500">No permission changes recorded</p>
              ) : (
                <div className="space-y-3">
                  {auditLog.map((entry: AuditEntry) => (
                    <div
                      key={entry.id}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {entry.action.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(entry.created_at).toLocaleString()}
                        </span>
                      </div>
                      {entry.previous_value && (
                        <p className="text-xs text-gray-500">
                          From: <code className="bg-gray-200 px-1 rounded">{entry.previous_value}</code>
                        </p>
                      )}
                      {entry.new_value && (
                        <p className="text-xs text-gray-500">
                          To: <code className="bg-gray-200 px-1 rounded">{entry.new_value}</code>
                        </p>
                      )}
                      {entry.reason && (
                        <p className="text-xs text-gray-600 mt-1">
                          Reason: {entry.reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            {t('userPermissions.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
}
